// ManualRecordDialog.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  InputAdornment,
  Box,
  Divider,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Card,
  CardContent,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableRow
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  Save,
  NavigateNext,
  Person,
  Phone,
  AccountBalance,
  Add,
  Delete,
  CheckCircle
} from '@mui/icons-material';
import Others from './Others';
import { fetchMemberFinancialDetails, allocateFunds, transferFunds } from './api';
import PostAllocationDialog from './PostAllocationDialog';
import { groupsApi } from '../Groups/api';
import apiService from '../Transactions/api';

const ManualRecordDialog = ({ 
  open, 
  onClose, 
  groups, 
  usersData, 
  onAllocate 
}) => {
  const [selectedGroup, setSelectedGroup] = useState('');
  const [groupMembers, setGroupMembers] = useState([]);
  const [currentMemberIndex, setCurrentMemberIndex] = useState(0);
  const [allocation, setAllocation] = useState({
    savings: 0,
    loan_repayment: 0,
    amount_for_advance_payment: 0,
  });
  
  // Updated others state to support multiple items like AllocateFundsDialog
  const [othersData, setOthersData] = useState({
    items: [],
    totalAmount: 0,
    descriptions: ''
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [showPostAllocation, setShowPostAllocation] = useState(false);
  const [allMembersCompleted, setAllMembersCompleted] = useState(false);
  const [cashCollected, setCashCollected] = useState(0);
  const [cashCollecting, setCashCollecting] = useState(false);
  const [cashCollectedConfirmed, setCashCollectedConfirmed] = useState(false);
  const [memberFinancialData, setMemberFinancialData] = useState({});
  
  // State for advance payment preview
  const [advancePreview, setAdvancePreview] = useState(null);

  const [periodData, setPeriodData] = useState({
    period_start: null,
    period_end: null,
    status: null,
    period_name: 'Loading...'
  });

  // Handle others data change
const handleOthersChange = (data) => {
  setOthersData(data);
};

const currentMember = groupMembers[currentMemberIndex];


  // Filter members by selected group
  useEffect(() => {
    if (selectedGroup && usersData) {
      const members = usersData.filter(member => 
        member.group_id === selectedGroup || member.group === selectedGroup
      );
      setGroupMembers(members);
      // Only reset to 0 if we're changing groups, not when updating members
      if (groupMembers.length === 0) {
        setCurrentMemberIndex(0);
      }
      resetAllocation();
    }
  }, [selectedGroup, usersData]); // Remove groupMembers from dependency array if it's there

  useEffect(() => {
    const loadMemberFinancialData = async () => {
      if (currentMember) {
        try {
          const data = await fetchMemberFinancialDetails(currentMember.id);
          setMemberFinancialData(data);
        } catch (error) {
          console.error('Error loading member financial data:', error);
          // Fallback to member data if financial details fail
          setMemberFinancialData({
            unallocated_funds: currentMember.unallocated_funds || 0
          });
        }
      }
    };
  
    loadMemberFinancialData();
  }, [currentMember]);

  // Calculate advance payment preview when advance payment amount changes
  useEffect(() => {
    const currentMember = groupMembers[currentMemberIndex];
    if (currentMember && allocation.amount_for_advance_payment > 0) {
      const currentAdvanceBalance = currentMember.advance_loan_balance || 0;
      if (currentAdvanceBalance > 0) {
        const preview = calculateAdvancePaymentSplit(currentAdvanceBalance, allocation.amount_for_advance_payment);
        setAdvancePreview(preview);
      } else {
        setAdvancePreview(null);
      }
    } else {
      setAdvancePreview(null);
    }
  }, [allocation.amount_for_advance_payment, currentMemberIndex, groupMembers]);

  useEffect(() => {
    if (groupMembers.length > 0) {
      // Check if we've processed all members (you can track this with a completion status)
      // For now, we'll use the current index reaching the end as completion
      const completed = currentMemberIndex === groupMembers.length - 1;
      setAllMembersCompleted(completed);
    }
  }, [currentMemberIndex, groupMembers.length]);

  // Fetch current period data when group is selected
useEffect(() => {
  const fetchPeriodData = async () => {
    if (selectedGroup) {
      try {
        const response = await groupsApi.getCurrentPeriodDates(selectedGroup);
        setPeriodData(response);
      } catch (error) {
        console.error('Error fetching period data:', error);
        setPeriodData({
          period_start: null,
          period_end: null,
          status: 'unknown',
          period_name: 'Period information unavailable'
        });
      }
    }
  };

  fetchPeriodData();
}, [selectedGroup]);

  // Calculate advance payment split (from AllocateFundsDialog logic)
  const calculateAdvancePaymentSplit = (currentBalance, paymentAmount) => {
    if (paymentAmount <= 0 || currentBalance <= 0) {
      return {
        pay_advance: 0,
        pay_advance_interest: 0,
        remaining_balance: currentBalance,
        is_valid: true,
        has_penalty: false,
        penalty_amount: 0,
        minimum_required_amount: 0
      };
    }

    // If paying more than the balance, limit to balance
    const actualPayment = Math.min(paymentAmount, currentBalance);
    
    // Calculate remaining balance after advance payment
    const remainingBalance = currentBalance - actualPayment;
    
    if (remainingBalance > 0) {
      // Calculate new principal and interest for remaining balance
      const newPrincipal = remainingBalance / 1.1;
      const interestAmount = newPrincipal * 0.1 + 10;
      const roundedInterest = Math.round(interestAmount);
      
      // Check if payment amount matches required payment + interest
      const requiredTotal = actualPayment + roundedInterest;
      const hasValidInterest = Math.abs(paymentAmount - requiredTotal) < 1; // Allow small rounding differences
      
      return {
        pay_advance: actualPayment,
        pay_advance_interest: roundedInterest,
        remaining_balance: remainingBalance,
        is_valid: hasValidInterest,
        has_penalty: !hasValidInterest && paymentAmount < requiredTotal,
        penalty_amount: hasValidInterest ? 0 : Math.max(0, requiredTotal - paymentAmount),
        minimum_required_amount: requiredTotal,
        message: hasValidInterest ? '' : `You must pay exactly ${formatCurrency(requiredTotal)} (${formatCurrency(actualPayment)} principal + ${formatCurrency(roundedInterest)} interest)`
      };
    } else {
      // Paying off completely
      return {
        pay_advance: actualPayment,
        pay_advance_interest: 0,
        remaining_balance: 0,
        is_valid: true,
        has_penalty: false,
        penalty_amount: 0,
        minimum_required_amount: actualPayment
      };
    }
  };

  // Reset allocation form
  const resetAllocation = () => {
    setAllocation({
      savings: 0,
      loan_repayment: 0,
      amount_for_advance_payment: 0,
      registration_fee: 0
    });
    setOthersData({
      items: [],
      totalAmount: 0,
      descriptions: ''
    });
    setCashCollected(0);
    setCashCollectedConfirmed(false); // Add this
    setAdvancePreview(null);
  };

  const handleCashCollection = async () => {
    if (!currentMember || cashCollected <= 0) return;
  
    setCashCollecting(true);
    try {
      const cashData = {
        memberId: currentMember.id,
        officerName: 'OfficeDesk',
        amount: cashCollected,
        remarks: `Cash collection for period: ${periodData.period_name}`
      };
  
      await apiService.collectCash(cashData);
      setCashCollectedConfirmed(true);
      setCashCollected(0);

      const updatedFinancialData = await fetchMemberFinancialDetails(currentMember.id);
      setMemberFinancialData(updatedFinancialData);
      
      alert('Cash collection recorded successfully!');
    } catch (error) {
      console.error('Error collecting cash:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setCashCollecting(false);
    }
  };

  // Handle input change for allocation fields
  const handleChange = (field, value) => {
    const numValue = parseFloat(value) || 0;
    setAllocation({
      ...allocation,
      [field]: numValue
    });
  };

  // Handle others items management
  const addOthersItem = () => {
    setOthersData({
      ...othersData,
      items: [...othersData.items, { description: '', amount: 0 }]
    });
  };

  const removeOthersItem = (index) => {
    const newItems = othersData.items.filter((_, i) => i !== index);
    const newTotal = newItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    setOthersData({
      ...othersData,
      items: newItems,
      totalAmount: newTotal,
      descriptions: newItems.map(item => item.description).filter(desc => desc.trim()).join(', ')
    });
  };

  const updateOthersItem = (index, field, value) => {
    const newItems = [...othersData.items];
    if (field === 'amount') {
      newItems[index][field] = parseFloat(value) || 0;
    } else {
      newItems[index][field] = value;
    }
    
    const newTotal = newItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const newDescriptions = newItems.map(item => item.description).filter(desc => desc.trim()).join(', ');
    
    setOthersData({
      items: newItems,
      totalAmount: newTotal,
      descriptions: newDescriptions
    });
  };

  // Get current member
  
  // Calculate totals with cash collected
  const totalAllocated = Object.values(allocation).reduce((sum, value) => sum + value, 0) + othersData.totalAmount;
  const baseUnallocatedFunds = memberFinancialData?.unallocated_funds || currentMember?.unallocated_funds || 0;
  const totalRepaid = baseUnallocatedFunds + (cashCollectedConfirmed ? cashCollected : 0);
  const remaining = totalRepaid - totalAllocated;

  // Get selected group details
  const selectedGroupData = groups.find(group => group.id === selectedGroup);

  // Calculate new balances after allocation
  const calculateNewBalances = () => {
    if (!currentMember) return {};
    
    const newSavings = (currentMember.current_savings || 0) + allocation.savings;
    const newLoanBalance = Math.max(0, (currentMember.loan_balance || 0) - allocation.loan_repayment);
    
    // For advance loan, use the preview calculation
    const newAdvanceBalance = advancePreview ? advancePreview.remaining_balance : 
      Math.max(0, (currentMember.advance_loan_balance || 0) - allocation.amount_for_advance_payment);
    
    return {
      newSavings,
      newLoanBalance,
      newAdvanceBalance
    };
  };

  const { newSavings, newLoanBalance, newAdvanceBalance } = calculateNewBalances();

  // Handle save allocation
  const handleSave = async () => {
    if (!currentMember) return;

    setSubmitting(true);
    try {
      const allocationData = {
        savings: allocation.savings,
        loan_repayment: allocation.loan_repayment,
        registration_fee: allocation.registration_fee,
        amount_for_advance_payment: allocation.amount_for_advance_payment,
        other: othersData.totalAmount,
        other_description: othersData.descriptions,
        other_items: othersData.items,
        cash_collected: cashCollected,
        confirmed: true
      };

      console.log('Submitting allocation for member:', currentMember.id, allocationData);
      await onAllocate(currentMember.id, allocationData);
      
      // Update the member's data in the local state to reflect the changes
      const updatedMembers = [...groupMembers];
      updatedMembers[currentMemberIndex] = {
        ...currentMember,
        current_savings: newSavings,
        loan_balance: newLoanBalance,
        advance_loan_balance: newAdvanceBalance,
        unallocated_funds: remaining < 0 ? 0 : remaining // Update unallocated funds
      };
      setGroupMembers(updatedMembers);
      
      resetAllocation();
      
    } catch (error) {
      console.error('Error saving allocation:', error);
      const errorMessage = error.response?.data?.error || error.message || "Failed to allocate funds";
      alert(`Error: ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveAndNext = async () => {
    if (!currentMember) return;
  
    setSubmitting(true);
    try {
      const allocationData = {
        savings: allocation.savings,
        loan_repayment: allocation.loan_repayment,
        registration_fee: allocation.registration_fee,
        amount_for_advance_payment: allocation.amount_for_advance_payment,
        other: othersData.totalAmount,
        other_description: othersData.descriptions,
        other_items: othersData.items,
        cash_collected: cashCollected,
        confirmed: true
      };
  
      console.log('Submitting allocation for member:', currentMember.id, allocationData);
      await onAllocate(currentMember.id, allocationData);
      
      // Update the member's data in the local state
      const updatedMembers = [...groupMembers];
      updatedMembers[currentMemberIndex] = {
        ...currentMember,
        current_savings: newSavings,
        loan_balance: newLoanBalance,
        advance_loan_balance: newAdvanceBalance,
        unallocated_funds: remaining < 0 ? 0 : remaining
      };
      setGroupMembers(updatedMembers);
      
      // Reset allocation first
      resetAllocation();
      
      // Then navigate to next member
      const nextIndex = currentMemberIndex + 1;
      if (nextIndex < groupMembers.length) {
        setCurrentMemberIndex(nextIndex);
      } else {
        setShowPostAllocation(true);
      }
      
    } catch (error) {
      console.error('Error saving allocation:', error);
      const errorMessage = error.response?.data?.error || error.message || "Failed to allocate funds";
      alert(`Error: ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Navigate to previous member
  const handlePrevious = () => {
    if (currentMemberIndex > 0) {
      setCurrentMemberIndex(currentMemberIndex - 1);
      resetAllocation();
    }
  };

  // Navigate to next member
  const handleNext = () => {
    if (currentMemberIndex < groupMembers.length - 1) {
      setCurrentMemberIndex(currentMemberIndex + 1);
      resetAllocation();
    }
  };

  // Add handlers for the post-allocation dialog
  const handleDisburseLoan = async (loanId) => {
    // Implement loan disbursement logic
    // This would call your disburseLoan API function
    console.log('Disbursing loan:', loanId);
    // await disburseLoan({ loan_id: loanId });
  };

  const handleCancelLoan = async (loanId) => {
    // Implement loan cancellation logic
    console.log('Cancelling loan:', loanId);
    // await cancelLoan({ loan_id: loanId });
  };

  const handleSaveGroupOthers = async (groupId, othersData) => {
    // Implement group others saving logic
    console.log('Saving group others:', { groupId, othersData });
    // This would be a new API endpoint for saving group-level collections
  };

  const handleViewPerformanceAnalysis = (group) => {
    // Handle performance analysis view
    console.log('Viewing performance analysis for group:', group);
    // This could open another dialog or navigate to a performance page
    alert(`Performance analysis for ${group.name} - Feature to be implemented`);
  };

  const handlePostAllocationClose = () => {
    setShowPostAllocation(false);
    // Optionally close the main dialog too
    handleDialogClose();
  };

  // Validate form
  const isValidForm = () => {
    if (totalAllocated <= 0 || remaining < 0) return false;
    
    // Check if cash collection is confirmed when cash is entered
    if (cashCollected > 0 && !cashCollectedConfirmed) return false;
    
    // Check if others data has valid amounts for all items
    if (othersData.items.some(item => !item.amount || item.amount <= 0)) return false;
    
    return true;
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Handle dialog close
  const handleDialogClose = () => {
    setSelectedGroup('');
    setGroupMembers([]);
    setCurrentMemberIndex(0);
    setPeriodData({ // Add this
      period_start: null,
      period_end: null,
      status: null,
      period_name: 'Loading...'
    });
    resetAllocation();
    onClose();
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleDialogClose} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: { 
            height: '90vh',
            maxHeight: '90vh',
            width: '95%',
            maxWidth: '1200px',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        {!selectedGroup ? (
          // Group Selection Screen
          <>
            <DialogTitle sx={{ pb: 1 }}>Select Group for Manual Record</DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
              <Box sx={{ py: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Select Group</InputLabel>
                  <Select
                    value={selectedGroup}
                    label="Select Group"
                    onChange={(e) => setSelectedGroup(e.target.value)}
                  >
                    {groups.map(group => (
                      <MenuItem key={group.id} value={group.id}>
                        {group.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </DialogContent>
            <DialogActions sx={{ pt: 1, pb: 2 }}>
              <Button onClick={handleDialogClose}>Cancel</Button>
            </DialogActions>
          </>
        ) : (
          // Allocation Screen
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* Group and Period Info */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h5" sx={{ mb: 0.5 }}>
                      Manual Record - {selectedGroupData?.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Period: {periodData.period_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Allocations for this period • Status: {periodData.status || 'Loading...'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip 
                      label={`${currentMemberIndex + 1} of ${groupMembers.length}`}
                      color="primary"
                      variant="outlined"
                    />
                    {allMembersCompleted && (
                      <Chip 
                        label="All Members Processed" 
                        color="success" 
                        icon={<CheckCircle />}
                      />
                    )}
                  </Box>
                </Box>
                
                {/* Member Info - Now in Header */}
                {currentMember && (
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Grid container alignItems="center" spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Person color="primary" fontSize="small" />
                          <Typography variant="h6" fontWeight="medium">
                            {currentMember.name}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Phone color="action" fontSize="small" />
                          <Typography variant="body2">{currentMember.phone}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                          <AccountBalance color="primary" fontSize="small" />
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              Unallocated: {formatCurrency(baseUnallocatedFunds)}
                            </Typography>
                            <Typography variant="body1" fontWeight="medium" sx={{ fontSize: '0.875rem' }}>
                              Total Repaid: {formatCurrency(totalRepaid)}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                )}
              </Box>
            </DialogTitle>

            <DialogContent sx={{ pt: 1, pb: 1, flex: 1, overflow: 'auto' }}>
              {currentMember && (
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  {/* Allocation Form */}
                  <Box sx={{ flex: 1 }}>
                    <Grid container spacing={3}>
                      {/* Cash Collection Field - Full Width */}
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                          <TextField
                            fullWidth
                            label="Cash Paid by Member for This Period"
                            type="number"
                            InputProps={{
                              startAdornment: <InputAdornment position="start">KES</InputAdornment>,
                            }}
                            value={cashCollected || ''}
                            onChange={(e) => {
                              setCashCollected(parseFloat(e.target.value) || 0);
                              setCashCollectedConfirmed(false); // Reset confirmation when amount changes
                            }}
                            disabled={submitting || cashCollecting}
                            helperText={`Cash to be collected from ${currentMember?.name || 'member'} for ${periodData.period_name}`}
                            sx={{ 
                              bgcolor: cashCollectedConfirmed ? 'success.50' : 'primary.50',
                              flex: 1
                            }}
                            error={cashCollected > 0 && !cashCollectedConfirmed}
                          />
                          <Button
                            variant="contained"
                            color="success"
                            onClick={handleCashCollection}
                            disabled={
                              cashCollected <= 0 || 
                              cashCollecting || 
                              cashCollectedConfirmed ||
                              submitting
                            }
                            sx={{ 
                              minWidth: '100px',
                              height: '56px', // Match TextField height
                              mt: 0
                            }}
                          >
                            {cashCollecting ? 'Processing...' : cashCollectedConfirmed ? 'Done ✓' : 'Collect'}
                          </Button>
                        </Box>
                        
                        {cashCollected > 0 && !cashCollectedConfirmed && (
                          <Typography 
                            variant="caption" 
                            color="warning.main" 
                            sx={{ mt: 1, display: 'block' }}
                          >
                            Click "Collect" to record cash payment before proceeding with allocation
                          </Typography>
                        )}
                      </Grid>

                      {/* Allocation Fields */}
                      <Grid item xs={12} sm={6}>
                        <Box>
                          <TextField
                            fullWidth
                            label="Allocate to Savings"
                            type="number"
                            InputProps={{
                              startAdornment: <InputAdornment position="start">KES</InputAdornment>,
                            }}
                            value={allocation.savings || ''}
                            onChange={(e) => handleChange('savings', e.target.value)}
                            disabled={submitting}
                          />
                          <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              Current: {formatCurrency(currentMember?.current_savings || 0)} → 
                              New: {formatCurrency(newSavings)}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box>
                          <TextField
                            fullWidth
                            label="Loan Repayment"
                            type="number"
                            InputProps={{
                              startAdornment: <InputAdornment position="start">KES</InputAdornment>,
                            }}
                            value={allocation.loan_repayment || ''}
                            onChange={(e) => handleChange('loan_repayment', e.target.value)}
                            disabled={submitting}
                          />
                          <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              Current: {formatCurrency(currentMember?.loan_balance || 0)} → 
                              New: {formatCurrency(newLoanBalance)}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box>
                          <TextField
                            fullWidth
                            label="Advance Payment"
                            type="number"
                            InputProps={{
                              startAdornment: <InputAdornment position="start">KES</InputAdornment>,
                            }}
                            value={allocation.amount_for_advance_payment || ''}
                            onChange={(e) => handleChange('amount_for_advance_payment', e.target.value)}
                            disabled={submitting}
                            error={advancePreview && !advancePreview.is_valid}
                            helperText="System will determine advance payment and interest allocation"
                          />
                          <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              Current: {formatCurrency(currentMember?.advance_loan_balance || 0)} → 
                              New: {formatCurrency(newAdvanceBalance)}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        
                      </Grid>
                    </Grid>

                    {/* Advance Payment Preview */}
                    {advancePreview && allocation.amount_for_advance_payment > 0 && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="h6" gutterBottom>
                          Advance Payment Preview
                        </Typography>
                        <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                              <Typography variant="body2" color="text.secondary">
                                Principal Payment
                              </Typography>
                              <Typography variant="h6">
                                {formatCurrency(advancePreview.pay_advance)}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Typography variant="body2" color="text.secondary">
                                Interest Payment
                              </Typography>
                              <Typography variant="h6">
                                {formatCurrency(advancePreview.pay_advance_interest)}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Typography variant="body2" color="text.secondary">
                                Remaining Balance
                              </Typography>
                              <Typography variant="h6" color={advancePreview.remaining_balance === 0 ? 'success.main' : 'text.primary'}>
                                {formatCurrency(advancePreview.remaining_balance)}
                                {advancePreview.remaining_balance === 0 && (
                                  <Chip 
                                    label="PAID OFF" 
                                    color="success" 
                                    size="small" 
                                    sx={{ ml: 1 }}
                                  />
                                )}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Paper>
                      </Box>
                    )}

                    {/* Advance Payment Error/Warning */}
                    {advancePreview && advancePreview.has_penalty && (
                      <Box sx={{ mt: 2 }}>
                        <Alert severity="warning">
                          <Typography variant="body2">
                            Submitting this amount ({formatCurrency(allocation.amount_for_advance_payment)}) will lead to a penalty of {formatCurrency(advancePreview.penalty_amount)}.
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            Minimum required amount to avoid penalty: {formatCurrency(advancePreview.minimum_required_amount)}
                          </Typography>
                        </Alert>
                      </Box>
                    )}

                    {advancePreview && !advancePreview.is_valid && (
                      <Box sx={{ mt: 2 }}>
                        <Alert severity="error">
                          {advancePreview.message}
                        </Alert>
                      </Box>
                    )}

                    <Box sx={{ mt: 3 }}>
                      <Divider sx={{ mb: 2 }} />
                      <Others onOthersChange={handleOthersChange} />
                    </Box>
                  </Box>
                </Box>
              )}
            </DialogContent>

            <DialogActions sx={{ p: 3, borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                
                {/* Left Side - Navigation Controls */}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button 
                    onClick={handleDialogClose}
                    disabled={submitting}
                    size="small"
                  >
                    Close
                  </Button>
                  <IconButton
                    onClick={handlePrevious}
                    disabled={currentMemberIndex === 0 || submitting}
                    color="primary"
                    size="small"
                  >
                    <ArrowBack />
                  </IconButton>
                  <IconButton
                    onClick={handleNext}
                    disabled={currentMemberIndex === groupMembers.length - 1 || submitting}
                    color="primary"
                    size="small"
                  >
                    <ArrowForward />
                  </IconButton>
                </Box>

                {/* Center - Allocation Summary */}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  minWidth: '200px'
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 3, 
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        Allocated
                      </Typography>
                      <Typography variant="subtitle1" fontWeight="medium">
                        {formatCurrency(totalAllocated)}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        Remaining
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        sx={{
                          color: remaining < 0 ? 'error.main' : remaining > 0 ? 'warning.main' : 'success.main'
                        }}
                      >
                        {formatCurrency(remaining)}
                      </Typography>
                    </Box>
                  </Box>
                  
                  {/* Error/Info messages below summary */}
                  {remaining < 0 && (
                    <Typography variant="caption" color="error.main" sx={{ mt: 0.5, textAlign: 'center' }}>
                      Exceeds total by {formatCurrency(Math.abs(remaining))}
                    </Typography>
                  )}

                  {cashCollected > 0 && (
                    <Typography variant="caption" color="info.main" sx={{ mt: 0.5, textAlign: 'center' }}>
                      +{formatCurrency(cashCollected)} cash collected
                    </Typography>
                  )}
                </Box>

                {/* Right Side - Action Buttons */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Save />}
                    onClick={handleSave}
                    disabled={!isValidForm() || submitting}
                    size="small"
                  >
                    {submitting ? 'Saving...' : 'Save'}
                  </Button>
                  {currentMemberIndex === groupMembers.length - 1 ? (
                    // Separate Save and Next Steps buttons for last member
                    <Button
                      variant="contained"
                      startIcon={<NavigateNext />}
                      onClick={() => setShowPostAllocation(true)}
                      disabled={submitting}
                      size="small"
                    >
                      Next Steps
                    </Button>
                  ) : (
                    // Save & Next button for other members
                    <Button
                      variant="contained"
                      startIcon={<NavigateNext />}
                      onClick={handleSaveAndNext}
                      disabled={
                        !isValidForm() || 
                        submitting
                      }
                      size="small"
                    >
                      {submitting ? 'Saving...' : 'Save & Next'}
                    </Button>
                  )}
                </Box>
              </Box>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Post Allocation Dialog */}
      <PostAllocationDialog
        open={showPostAllocation}
        onClose={handlePostAllocationClose}
        selectedGroup={selectedGroupData}
        groupMembers={groupMembers}
        onDisburseLoan={handleDisburseLoan}
        onCancelLoan={handleCancelLoan}
        onSaveGroupOthers={handleSaveGroupOthers}
        onViewPerformanceAnalysis={handleViewPerformanceAnalysis}
      />
    </>
  );
};

export default ManualRecordDialog;